import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { INDIAN_STATES } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Users, Trash2, Pencil, Search, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const formatINR = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", gstin: "", state: "", email: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statementClient, setStatementClient] = useState(null);
  const [statementForm, setStatementForm] = useState({ from_date: "", to_date: "" });
  const [downloadingStatement, setDownloadingStatement] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/clients"),
      api.get("/clients-balances").catch(() => ({ data: {} })),
    ])
      .then(([cr, br]) => {
        setClients(cr.data);
        setBalances(br.data || {});
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => setForm({ ...form, [field]: value });

  const openCreate = () => {
    setEditId(null);
    setForm({ name: "", gstin: "", state: "", email: "", phone: "", address: "" });
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditId(client.client_id);
    setForm({
      name: client.name,
      gstin: client.gstin,
      state: client.state,
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
    });
    setDialogOpen(true);
  };

  const saveClient = async () => {
    if (!form.name || !form.state) {
      toast.error("Name and state are required");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const response = await api.put(`/clients/${editId}`, form);
        setClients(clients.map((c) => (c.client_id === editId ? response.data : c)));
        toast.success("Client updated!");
      } else {
        const response = await api.post("/clients", form);
        setClients([response.data, ...clients]);
        toast.success("Client added!");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      setClients(clients.filter((c) => c.client_id !== id));
      toast.success("Client deleted");    } catch {
      toast.error("Failed to delete");
    }
  };

  const openStatement = (client) => {
    // Default range: last 90 days → today
    const today = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    setStatementForm({
      from_date: from.toISOString().split("T")[0],
      to_date: today.toISOString().split("T")[0],
    });
    setStatementClient(client);
  };

  const downloadStatement = async () => {
    if (!statementClient) return;
    setDownloadingStatement(true);
    try {
      const params = new URLSearchParams();
      if (statementForm.from_date) params.set("from_date", statementForm.from_date);
      if (statementForm.to_date) params.set("to_date", statementForm.to_date);
      const url = `/clients/${statementClient.client_id}/statement.pdf?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `statement_${(statementClient.name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Statement downloaded");
      setStatementClient(null);
    } catch {
      toast.error("Failed to generate statement");
    } finally {
      setDownloadingStatement(false);
    }
  };

  const filtered = clients.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.gstin?.toLowerCase().includes(q) || c.state?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Clients</h1>
          <p className="text-sm text-[#64748b] mt-1">{clients.length} clients</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/clients/import")} data-testid="import-clients-btn" className="border-[#e2e8f0] rounded-xl">
            <Upload className="h-4 w-4 mr-2" /> Import
          </Button>
          <Button onClick={openCreate} data-testid="add-client-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
            <Plus className="h-4 w-4 mr-2" /> Add Client
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
        <Input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, GSTIN, state..."
          data-testid="clients-search-input"
          className="pl-9 h-10 border-[#e2e8f0]"
        />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{editId ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">Fill in client details and state for GST</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="client-name-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">GSTIN</Label>
                <Input value={form.gstin} onChange={(e) => handleChange("gstin", e.target.value)} placeholder="22AAAAA0000A1Z5" className="mt-1.5 font-mono text-sm" data-testid="client-gstin-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">State *</Label>
                <Select value={form.state} onValueChange={(v) => handleChange("state", v)}>
                  <SelectTrigger data-testid="client-state-select" className="mt-1.5">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_STATES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Email</Label>
                <Input value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-medium">Phone</Label>
                <Input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Address</Label>
              <Input value={form.address} onChange={(e) => handleChange("address", e.target.value)} className="mt-1.5" />
            </div>
            <Button onClick={saveClient} disabled={saving} data-testid="save-client-btn" className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
              {saving ? "Saving..." : editId ? "Update Client" : "Add Client"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748b]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">{search ? "No clients match your search." : "No clients yet. Add your first client to get started."}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">GSTIN</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">State</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Outstanding Balance</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Email</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((client) => {
                  const bal = balances[client.client_id] || { outstanding: 0, advance_balance: 0, net_balance: 0 };
                  const net = bal.net_balance;
                  const isAdvance = net < -0.01;
                  const hasDue = net > 0.01;
                  return (
                    <tr key={client.client_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`client-row-${client.client_id}`}>
                      <td className="py-3 px-4 text-[#0f172a] font-medium">{client.name}</td>
                      <td className="py-3 px-4 text-[#64748b] font-mono text-xs">{client.gstin || "-"}</td>
                      <td className="py-3 px-4 text-[#64748b]">{client.state}</td>
                      <td className="py-3 px-4 text-right" data-testid={`balance-${client.client_id}`}>
                        {hasDue ? (
                          <span className="font-mono text-sm font-semibold text-[#ef4444]">{formatINR(net)}</span>
                        ) : isAdvance ? (
                          <span className="font-mono text-sm font-semibold text-[#10b981]" title="Advance held">{formatINR(net)}</span>
                        ) : (
                          <span className="font-mono text-sm text-[#94a3b8]">{formatINR(0)}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[#64748b]">{client.email || "-"}</td>
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openStatement(client)} title="Download Statement of Account" data-testid={`statement-client-${client.client_id}`}>
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(client)} data-testid={`edit-client-${client.client_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteClient(client.client_id)} className="text-red-500" data-testid={`delete-client-${client.client_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Statement of Account dialog */}
      <Dialog open={!!statementClient} onOpenChange={(open) => !open && !downloadingStatement && setStatementClient(null)}>
        <DialogContent className="max-w-md" data-testid="statement-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#4338ca]" /> Statement of Account
            </DialogTitle>
            <DialogDescription>
              Download a chronological ledger PDF with running balance.
            </DialogDescription>
          </DialogHeader>
          {statementClient && (
            <div className="space-y-4 mt-2">
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3">
                <p className="text-xs text-[#64748b]">Account of</p>
                <p className="font-outfit font-semibold text-[#0f172a]">{statementClient.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-[#64748b]">From date</Label>
                  <Input
                    type="date"
                    value={statementForm.from_date}
                    onChange={(e) => setStatementForm({ ...statementForm, from_date: e.target.value })}
                    data-testid="statement-from-date"
                    className="mt-1 border-[#e2e8f0]"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#64748b]">To date</Label>
                  <Input
                    type="date"
                    value={statementForm.to_date}
                    onChange={(e) => setStatementForm({ ...statementForm, to_date: e.target.value })}
                    data-testid="statement-to-date"
                    className="mt-1 border-[#e2e8f0]"
                  />
                </div>
              </div>
              <p className="text-[11px] text-[#94a3b8] italic">
                Leave dates empty to include all transactions.
              </p>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStatementClient(null)}
                  disabled={downloadingStatement}
                  className="rounded-lg border-[#e2e8f0]"
                  data-testid="cancel-statement-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={downloadStatement}
                  disabled={downloadingStatement}
                  className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg"
                  data-testid="download-statement-btn"
                >
                  {downloadingStatement ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
